// ─── src/components/dashboard/ReportLeadsTable.tsx ───────────────────────────

import { Trash2 } from "lucide-react";
import type { ReportLead } from "../../types/reportLead";

interface Props {
  rows: ReportLead[];
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

const SLUG_LABEL: Record<string, string> = {
  "rising-from-the-rubble":                     "Rising From the Rubble",
  "covid-19-survival-guide":                    "COVID-19 Survival Guide",
  "entrepreneurship-in-exile":                  "Entrepreneurship in Exile",
  "entrepreneurship-in-conflict-zones":         "Entrepreneurship in Conflict Zones",
  "green-entrepreneurship-recovery":            "Green Entrepreneurship",
  "addressing-syria-entrepreneurship-skills-gap": "Syria Skills Gap",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day:   "2-digit",
    month: "short",
    year:  "numeric",
  });
}

function initials(firstName: string, lastName: string) {
  return `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
}

const AVATAR_COLORS = [
  "bg-brand-teal",
  "bg-brand-orange",
  "bg-purple-500",
  "bg-blue-500",
  "bg-pink-500",
];

function avatarColor(email: string) {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function ReportLeadsTable({ rows, onDelete, isDeleting }: Props) {
  if (rows.length === 0) {
    return (
      <div className="card p-10 text-center text-brand-muted">
        No report leads yet.
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border">
              <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Name</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Email</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Report</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Downloaded</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((lead, idx) => (
              <tr
                key={lead.id}
                className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
              >
                <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>

                {/* Name + avatar */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center
                                    text-xs font-bold text-white flex-shrink-0
                                    ${avatarColor(lead.email)}`}>
                      {initials(lead.firstName, lead.lastName)}
                    </div>
                    <span className="text-white font-medium whitespace-nowrap">
                      {lead.firstName} {lead.lastName}
                    </span>
                  </div>
                </td>

                <td className="px-4 py-3 text-brand-muted">{lead.email}</td>

                {/* Report badge */}
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-brand-teal/20 text-brand-tealLight whitespace-nowrap">
                    {SLUG_LABEL[lead.reportSlug] ?? lead.reportSlug}
                  </span>
                </td>

                <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                  {formatDate(lead.createdAt)}
                </td>

                <td className="px-4 py-3">
                  <button
                    onClick={() => onDelete(lead.id)}
                    disabled={isDeleting}
                    title="Remove lead"
                    className="p-1.5 rounded-lg text-brand-muted hover:text-red-400
                               hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
